<template>
    <div class="content-activity">
        <template v-if="question.type == utils.ACTIVITY_TYPE.IMAGE">
            <div class="content-media">
                <img :src="question.media_url" class="img-fluid content-image" alt="">
            </div>
        </template>

        <!-- Shown on the room's big screen only: phones just point there
             (entering still counts as seen, see mark_viewed). -->
        <div v-if="question.type == utils.ACTIVITY_TYPE.VIDEO && on_big_screen_only" class="content-screen-only">
            <i class="bi bi-display"></i>
            <div class="content-screen-only-title">Mira la pantalla grande</div>
            <div>El vídeo se está proyectando en la sala.</div>
        </div>

        <template v-else-if="question.type == utils.ACTIVITY_TYPE.VIDEO && video_source">
            <!-- The player mounts (and loads) right away, but stays hidden
                 behind the overlay until it's time to play - see play_at. -->
            <div class="content-media content-video">
                <VideoPlayer ref="player" :source="video_source" @ready="on_video_ready" />
                <div v-if="!video_started" class="content-video-wait">
                    <span class="spinner-border mb-3"></span>
                    <div v-if="!video_ready">Cargando vídeo...</div>
                    <template v-else-if="media_sync">
                        <div>{{ media_sync.audience == utils.MEDIA_DISPLAY.SCREEN ? 'Preparando la pantalla grande' : 'Esperando al resto de equipos' }}</div>
                        <div class="content-video-wait-count">{{ media_sync.ready.length }} / {{ media_sync.total }}</div>
                    </template>
                    <div v-else>¡Preparados!</div>
                </div>
            </div>
        </template>

        <template v-if="question.type == utils.ACTIVITY_TYPE.PDF">
            <!-- Mobile browsers mostly can't render a PDF inline (iOS shows
                 page 1 only, Android Chrome nothing) - the button always
                 works, the inline viewer is a bonus where supported. -->
            <div class="content-media content-pdf">
                <iframe :src="question.media_url"></iframe>
            </div>
            <div class="text-center mt-3">
                <a :href="question.media_url" target="_blank" rel="noopener" class="btn btn-primary"><i class="bi bi-file-earmark-pdf me-1"></i>Abrir PDF</a>
            </div>
        </template>

        <template v-if="question.type == utils.ACTIVITY_TYPE.HTML">
            <div class="content-html" v-html="safe_html"></div>
        </template>

        <div class="content-description mt-3" v-if="question.description">{{ question.description }}</div>
    </div>
</template>

<script>
import DOMPurify from 'dompurify'
import VideoPlayer from '@/components/VideoPlayer.vue'

// Below this lag behind play_at a late player just starts from the
// beginning - seeking for a second or two isn't worth the jump.
const LATE_SEEK_THRESHOLD_S = 2;

export default {
    name: "Content",
    components: { VideoPlayer },
    data(){
        return {
            utils: require("../../../../shared/utils"),
            sent: false,
            video_ready: false,
            video_started: false,
            ready_sent_at: 0,
            play_timer: null,
        };
    },
    computed: {
        question(){
            return this.$store.state.game.question.question;
        },
        video_source(){
            return this.utils.get_video_source(this.question.media_url);
        },
        // Event mode only: the server's "is everyone's player loaded yet"
        // round for this video (see sv._await_media_ready). Online mode is
        // self-paced - there's nobody to wait for.
        synced(){
            return window.MODE != this.utils.APP_MODES.ONLINE;
        },
        // Event mode only: online there's no big screen, the player's own
        // device always plays it.
        on_big_screen_only(){
            const user = this.$store.state.game.user;
            return this.synced && this.question.display_on == this.utils.MEDIA_DISPLAY.SCREEN
                && !(user && user.role == this.utils.USER_ROLES.MANAGER);
        },
        media_sync(){
            const sync = this.$store.state.game.media_sync;
            return (sync && sync.question_id == this.question.id) ? sync : null;
        },
        // When everyone presses play. null = not yet decided. A launch with
        // no sync round but a running clock (e.g. reconnecting into a video
        // started before this existed) plays straight away.
        play_at(){
            if(!this.synced) return 0;
            if(this.media_sync) return this.media_sync.play_at;
            return this.question.end_time ? 0 : null;
        },
        // Admin-authored, but rendered on every player's device - strip
        // anything that could run script. iframes are kept for Quill's
        // embedded videos (YouTube/Vimeo players).
        safe_html(){
            return DOMPurify.sanitize(this.question.html || "", {
                ADD_TAGS: ['iframe'],
                ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'target'],
            });
        }
    },
    watch: {
        // Event mode: Title.vue navigates here a moment before the server
        // flips to CHOICES, and update_activity drops anything sent before
        // that - so retry once the state actually changes.
        '$store.state.game.state'(){
            this.mark_viewed();
        },
        // Fires on every CLIENT_UPDATE (the object is rebuilt each time) -
        // that's what lets a MEDIA_READY that arrived before the server
        // opened the round be re-sent (send_media_ready dedupes/throttles).
        media_sync(){
            this.send_media_ready();
        },
        play_at(){
            this.schedule_play();
        }
    },
    mounted(){
        this.mark_viewed();
    },
    beforeUnmount(){
        clearTimeout(this.play_timer);
    },
    methods: {
        user_activity(){
            const user = this.$store.state.game.user;
            const user_data = (this.$store.state.game.clients || []).find((cli) => user && cli.username == user.username);
            if(!user_data || !user_data.data) return null;
            return user_data.data.find((q) => q.question == this.$store.state.game.current_question);
        },
        on_video_ready(){
            this.video_ready = true;
            this.send_media_ready();
            this.schedule_play();
        },
        send_media_ready(){
            const sync = this.media_sync;
            const user = this.$store.state.game.user;
            if(!this.video_ready || !sync || sync.play_at || (user && sync.ready.includes(user.id)))
                return;
            if(Date.now() - this.ready_sent_at < 2000)
                return;
            this.ready_sent_at = Date.now();
            window.network.send_reliable(window.RPC.MEDIA_READY, { question_id: this.question.id });
        },
        schedule_play(){
            if(this.video_started || !this.video_ready || this.play_at == null)
                return;
            clearTimeout(this.play_timer);
            this.play_timer = setTimeout(this.start_video, Math.max(0, this.play_at - Date.now()));
        },
        start_video(){
            if(this.video_started || !this.$refs.player) return;
            this.video_started = true;
            const late = this.play_at ? (Date.now() - this.play_at) / 1000 : 0;
            this.$refs.player.play(late > LATE_SEEK_THRESHOLD_S ? late : 0);
        },
        // Entering the activity is what completes it - there's no reliable
        // way to know whether the player actually watched/read it.
        mark_viewed(){
            if(this.sent || this.user_activity() != null)
                return;
            if(this.$route.name == "Result")
                return;
            if(window.MODE != this.utils.APP_MODES.ONLINE && this.$store.state.game.state != this.utils.GAME_STATES.CHOICES)
                return;

            this.sent = true;
            window.network.send_reliable(window.RPC.UPDATE_ACTIVITY, {
                question: this.question.id,
                choice: [this.utils.CONTENT_VIEWED_CHOICE],
                locked: true,
                timestamp: Date.now()
            }, {
                onFail: () => { this.sent = false; }
            });
        }
    }
}
</script>

<style scoped>
    .content-media{
        display: flex;
        justify-content: center;
    }
    .content-image{
        max-height: 70vh;
        object-fit: contain;
        border-radius: 8px;
    }
    .content-video,
    .content-pdf{
        position: relative;
        width: 100%;
    }
    .content-video-wait{
        position: absolute;
        inset: 0;
        z-index: 5;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        border-radius: 8px;
        background-color: var(--vc-ink-900, #111);
        color: var(--vc-paper-50, #fff);
        font-family: "Archivo", sans-serif;
        font-weight: 700;
        text-align: center;
    }
    .content-video-wait-count{
        font-size: 2rem;
        font-weight: 900;
        margin-top: 4px;
    }
    .content-pdf iframe{
        width: 100%;
        height: 65vh;
        border: 0;
        border-radius: 8px;
        background-color: #fff;
    }
    .content-screen-only{
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
        gap: 6px;
        padding: 40px 20px;
        border-radius: 12px;
        background: var(--vc-ink-900, #17202b);
        color: var(--vc-paper-50, #fff);
    }
    .content-screen-only .bi{
        font-size: 3rem;
        color: var(--vc-accent-500, #00a7af);
    }
    .content-screen-only-title{
        font-family: "Archivo", sans-serif;
        font-weight: 800;
        font-size: 1.4rem;
        text-transform: uppercase;
    }
    .content-description{
        white-space: pre-line;
        font-size: 1.05rem;
    }
    .content-html{
        background-color: var(--vc-paper-50, #fff);
        border-radius: 8px;
        padding: 20px;
        overflow-wrap: anywhere;
    }
    .content-html :deep(img){
        max-width: 100%;
        height: auto;
    }
    .content-html :deep(iframe){
        max-width: 100%;
    }
    .content-html :deep(.ql-video){
        display: block;
        width: 100%;
        aspect-ratio: 16 / 9;
        border: 0;
    }
    .content-html :deep(.ql-align-center){ text-align: center; }
    .content-html :deep(.ql-align-right){ text-align: right; }
    .content-html :deep(.ql-align-justify){ text-align: justify; }
</style>
